import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Upload, Save, Eye, Edit2, Video, Image as ImageIcon, Plus, Trash2 } from 'lucide-react';
import { useAdmin } from '../../context/AdminContext';
import { getPageContent, updatePageContent, uploadMedia } from '../../services/api';

const PageEditor = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const data = await getPageContent();
      // Ensure all sections exist with defaults
      const fullContent = {
        ...data,
        services_section: data.services_section || { heading: '', description: '' },
        cta_section: data.cta_section || { 
          heading: '', description: '', 
          button1_text: '', button1_link: '',
          button2_text: '', button2_link: ''
        },
        stats: data.stats || {
          happy_customers: '',
          average_rating: '',
          chocolates_created: '',
          satisfaction: ''
        }
      };
      setContent(fullContent);
    } catch (error) {
      console.error('Error loading content:', error);
      alert('Failed to load page content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePageContent(content);
      alert('✅ Page content saved successfully!');
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Failed to save page content');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, section, field) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target.result;
        
        // For demo, we'll use the data URL directly
        // In production, upload to CDN
        updateContentField(section, field, dataUrl);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    }
  };

  const updateContentField = (section, field, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      
      if (section.includes('.')) {
        const [mainSection, subField] = section.split('.');
        newContent[mainSection] = {
          ...newContent[mainSection],
          [subField]: field ? { ...newContent[mainSection][subField], [field]: value } : value
        };
      } else {
        if (field) {
          newContent[section] = {
            ...newContent[section],
            [field]: value
          };
        } else {
          newContent[section] = value;
        }
      }
      
      return newContent;
    });
  };

  const updateTestimonialVideo = (index, field, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.testimonial_videos = [...newContent.testimonial_videos];
      newContent.testimonial_videos[index] = {
        ...newContent.testimonial_videos[index],
        [field]: value
      };
      return newContent;
    });
  };

  const addTestimonial = () => {
    setContent(prev => ({
      ...prev,
      testimonial_videos: [
        ...prev.testimonial_videos,
        {
          video_url: '',
          rating: 5,
          text: 'New testimonial text',
          customer_name: 'Customer Name',
          customer_title: 'Customer Title'
        }
      ]
    }));
  };

  const removeTestimonial = (index) => {
    setContent(prev => ({
      ...prev,
      testimonial_videos: prev.testimonial_videos.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading page editor...</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return <div className="min-h-screen flex items-center justify-center">Failed to load content</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Visual Page Editor</h1>
              <p className="text-sm text-gray-500">Click on sections to edit content and upload media</p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/edible-greetings/admin')}
              >
                ← Back to Admin
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open('/', '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <Card className="mb-8 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Hero Section</h2>
            <Edit2 className="w-5 h-5 text-purple-600" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Background Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Image
              </label>
              <div className="relative">
                {content.hero.background_image ? (
                  <div className="relative group">
                    <img 
                      src={content.hero.background_image} 
                      alt="Background" 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e.target.files[0], 'hero', 'background_image')}
                        />
                        <div className="bg-white text-gray-900 px-4 py-2 rounded-lg flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Change Image
                        </div>
                      </label>
                    </div>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-gray-300 rounded-lg p-12 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'hero', 'background_image')}
                    />
                    <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Click to upload background image</span>
                  </label>
                )}
              </div>
            </div>

            {/* Video Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hero Video
              </label>
              <div className="relative">
                {content.hero.video_url ? (
                  <div className="relative group">
                    <video 
                      src={content.hero.video_url} 
                      className="w-full h-48 object-cover rounded-lg"
                      controls
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="video/*"
                          className="hidden"
                          onChange={(e) => handleFileUpload(e.target.files[0], 'hero', 'video_url')}
                        />
                        <div className="bg-white text-gray-900 px-4 py-2 rounded-lg flex items-center gap-2">
                          <Upload className="w-4 h-4" />
                          Change Video
                        </div>
                      </label>
                    </div>
                  </div>
                ) : (
                  <label className="border-2 border-dashed border-gray-300 rounded-lg p-12 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 transition-colors">
                    <input
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'hero', 'video_url')}
                    />
                    <Video className="w-12 h-12 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">Click to upload video</span>
                  </label>
                )}
              </div>
            </div>
          </div>

          {/* Text Fields */}
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
              <input
                type="text"
                value={content.hero.heading}
                onChange={(e) => updateContentField('hero', 'heading', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subheading</label>
              <input
                type="text"
                value={content.hero.subheading}
                onChange={(e) => updateContentField('hero', 'subheading', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={content.hero.description}
                onChange={(e) => updateContentField('hero', 'description', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
              <input
                type="text"
                value={content.hero.tagline}
                onChange={(e) => updateContentField('hero', 'tagline', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
        </Card>

        {/* Service Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Service Card 1 */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Service Card 1</h2>
              <Edit2 className="w-5 h-5 text-purple-600" />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Image
              </label>
              {content.service_card_1.background_image ? (
                <div className="relative group">
                  <img 
                    src={content.service_card_1.background_image} 
                    alt="Service 1" 
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'service_card_1', 'background_image')}
                      />
                      <div className="bg-white text-gray-900 px-3 py-1.5 rounded text-sm flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Change
                      </div>
                    </label>
                  </div>
                </div>
              ) : (
                <label className="border-2 border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'service_card_1', 'background_image')}
                  />
                  <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Upload image</span>
                </label>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={content.service_card_1.title}
                  onChange={(e) => updateContentField('service_card_1', 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={content.service_card_1.description}
                  onChange={(e) => updateContentField('service_card_1', 'description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          </Card>

          {/* Service Card 2 */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Service Card 2</h2>
              <Edit2 className="w-5 h-5 text-purple-600" />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Image
              </label>
              {content.service_card_2.background_image ? (
                <div className="relative group">
                  <img 
                    src={content.service_card_2.background_image} 
                    alt="Service 2" 
                    className="w-full h-32 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'service_card_2', 'background_image')}
                      />
                      <div className="bg-white text-gray-900 px-3 py-1.5 rounded text-sm flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Change
                      </div>
                    </label>
                  </div>
                </div>
              ) : (
                <label className="border-2 border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e.target.files[0], 'service_card_2', 'background_image')}
                  />
                  <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Upload image</span>
                </label>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={content.service_card_2.title}
                  onChange={(e) => updateContentField('service_card_2', 'title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={content.service_card_2.description}
                  onChange={(e) => updateContentField('service_card_2', 'description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Testimonial Videos */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Testimonial Videos</h2>
              <p className="text-sm text-gray-500">Add customer video testimonials</p>
            </div>
            <Button onClick={addTestimonial} size="sm" className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Testimonial
            </Button>
          </div>

          <div className="space-y-6">
            {content.testimonial_videos.map((testimonial, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Testimonial {index + 1}</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeTestimonial(index)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Video
                    </label>
                    {testimonial.video_url ? (
                      <div className="relative group">
                        <video 
                          src={testimonial.video_url} 
                          className="w-full h-32 object-cover rounded-lg"
                          controls
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="video/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                const reader = new FileReader();
                                reader.onload = (e) => {
                                  updateTestimonialVideo(index, 'video_url', e.target.result);
                                };
                                reader.readAsDataURL(file);
                              }}
                            />
                            <div className="bg-white text-gray-900 px-3 py-1.5 rounded text-sm flex items-center gap-2">
                              <Upload className="w-4 h-4" />
                              Change
                            </div>
                          </label>
                        </div>
                      </div>
                    ) : (
                      <label className="border-2 border-dashed border-gray-300 rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 transition-colors">
                        <input
                          type="file"
                          accept="video/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              updateTestimonialVideo(index, 'video_url', e.target.result);
                            };
                            reader.readAsDataURL(file);
                          }}
                        />
                        <Video className="w-8 h-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">Upload video</span>
                      </label>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                      <input
                        type="text"
                        value={testimonial.customer_name}
                        onChange={(e) => updateTestimonialVideo(index, 'customer_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={testimonial.customer_title}
                        onChange={(e) => updateTestimonialVideo(index, 'customer_title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Testimonial Text</label>
                      <textarea
                        value={testimonial.text}
                        onChange={(e) => updateTestimonialVideo(index, 'text', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Services Section Heading */}
        <Card className="p-6 mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Services Section</h2>
            <Edit2 className="w-5 h-5 text-purple-600" />
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Heading</label>
              <input
                type="text"
                value={content.services_section?.heading || ''}
                onChange={(e) => updateContentField('services_section', 'heading', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
              <textarea
                value={content.services_section?.description || ''}
                onChange={(e) => updateContentField('services_section', 'description', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </Card>

        {/* CTA Section */}
        <Card className="p-6 mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Call-to-Action Section</h2>
            <Edit2 className="w-5 h-5 text-purple-600" />
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
              <input
                type="text"
                value={content.cta_section?.heading || ''}
                onChange={(e) => updateContentField('cta_section', 'heading', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={content.cta_section?.description || ''}
                onChange={(e) => updateContentField('cta_section', 'description', e.target.value)}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button 1 Text</label>
                <input
                  type="text"
                  value={content.cta_section?.button1_text || ''}
                  onChange={(e) => updateContentField('cta_section', 'button1_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button 1 Link</label>
                <input
                  type="text"
                  value={content.cta_section?.button1_link || ''}
                  onChange={(e) => updateContentField('cta_section', 'button1_link', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button 2 Text</label>
                <input
                  type="text"
                  value={content.cta_section?.button2_text || ''}
                  onChange={(e) => updateContentField('cta_section', 'button2_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Button 2 Link</label>
                <input
                  type="text"
                  value={content.cta_section?.button2_link || ''}
                  onChange={(e) => updateContentField('cta_section', 'button2_link', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Stats Section */}
        <Card className="p-6 mt-8 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Statistics Section</h2>
            <Edit2 className="w-5 h-5 text-purple-600" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Happy Customers</label>
              <input
                type="text"
                value={content.stats?.happy_customers || ''}
                onChange={(e) => updateContentField('stats', 'happy_customers', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Average Rating</label>
              <input
                type="text"
                value={content.stats?.average_rating || ''}
                onChange={(e) => updateContentField('stats', 'average_rating', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Chocolates Created</label>
              <input
                type="text"
                value={content.stats?.chocolates_created || ''}
                onChange={(e) => updateContentField('stats', 'chocolates_created', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Satisfaction</label>
              <input
                type="text"
                value={content.stats?.satisfaction || ''}
                onChange={(e) => updateContentField('stats', 'satisfaction', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PageEditor;
